import React, { useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import DropdownMenu from "../../ui/DropdownMenu";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import moment from "moment";
import { fetchAllKyc, resetSingleKyc } from "../../../redux/features/kycSlice";
import ViewKycModal from "./ViewKycModal";
import ApproveKycModal from "./ApproveKyc";
import RejectKycModal from "./RejectKyc";
import SearchInput from "../../../components/form/SearchInput";
import Button from "../../ui/Button";
import FilterByButton from "../../ui/FilterButton";
import { clearAnalyticsData } from "../../../redux/features/analyticsSlice";
import { kycStatusOption, verificationStatusOption } from "./util";

const KYCTable: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<any>();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [verificationStatus, setVerificationStatus] = useState();

  const dispatch = useAppDispatch();
  const { allKyc, loading } = useAppSelector((state) => state.kyc);

  const dropdownOptionsForKyc = [
    {
      label: "View Details",
      onClick: (user: any) => {
        setSelectedUser(user);
        setViewModalOpen(true);
      },
      returnAll: true,
    },
    {
      label: <span className="text-green-600">Approve</span>,
      onClick: (user: any) => {
        setSelectedUser(user);
        setApproveModalOpen(true);
      },
      returnAll: true,
    },
    {
      label: <span className="text-red-500">Reject</span>,
      onClick: (user: any) => {
        setSelectedUser(user);
        setRejectModalOpen(true);
      },
      returnAll: true,
    },
  ];

  const kycData = {
    columns: [
      { label: "Full Name", field: "fullName" },
      { label: "Email", field: "email" },
      { label: "Phone", field: "phone" },
      { label: "City", field: "city" },
      { label: "State", field: "state" },
      { label: "NIN", field: "nin" },
      { label: "BVN", field: "bvn" },
      { label: "CAC", field: "cac" },
      { label: "Beginner", field: "beginner" },
      { label: "Address Verified", field: "addressVerified" },
      { label: "BVN Verified", field: "bvnVerified" },
      { label: "NIN Verified", field: "ninVerified" },
      { label: "CAC Verified", field: "cacVerified" },
      { label: "Submitted On", field: "submittedAt" },
      { label: "KYC Status", field: "status" },
      { label: "Action", field: "action" },
    ],
    rows: allKyc?.records?.map((item: any, index: number) => ({
      id: index + 1,
      fullName: `${item?.user?.firstName ?? "N/A"} ${
        item?.user?.lastName ?? ""
      }`,
      email: item?.user?.email ?? "N/A",
      phone: item?.user?.phoneNumber ?? "N/A",
      city: item?.city ?? "N/A",
      state: item?.state ?? "N/A",
      nin: item?.nin ?? "N/A",
      bvn: item?.bvn ?? "N/A",
      cac: item?.cac ?? "N/A",
      beginner: (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            item?.beginner
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {item?.beginner ? "Yes" : "No"}
        </span>
      ),
      addressVerified: (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            item?.addressVerified
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {item?.addressVerified ? "Yes" : "No"}
        </span>
      ),
      bvnVerified: (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            item?.bvnVerified
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {item?.bvnVerified ? "Yes" : "No"}
        </span>
      ),
      ninVerified: (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            item?.ninVerified
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {item?.ninVerified ? "Yes" : "No"}
        </span>
      ),
      cacVerified: (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full ${
            item?.cacVerified
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {item?.cacVerified ? "Yes" : "No"}
        </span>
      ),
      submittedAt: moment(item?.createdAt).format("Do MMM YYYY"),
      status: (
        <div
          className={`px-3 text-center py-1 rounded-full text-sm font-semibold ${
            item?.user?.kycStatus === "APPROVED"
              ? "bg-green-100 text-green-700"
              : item?.user?.kycStatus === "REJECTED"
              ? "bg-red-100 text-red-700"
              : "bg-yellow-100 text-yellow-700"
          }`}
        >
          {item?.user?.kycStatus}
        </div>
      ),
      action: (
        <DropdownMenu
          options={dropdownOptionsForKyc}
          icon={<MoreVertical style={{ cursor: "pointer" }} />}
          item={item}
        />
      ),
    })),
  };

  const tabList = [
    {
      id: 1,
      name: "All KYC",
      count: allKyc?.pagination?.total ?? 0,
    },
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSearch(inputValue);
    }
  };

  useEffect(() => {
    dispatch(resetSingleKyc());
    dispatch(
      fetchAllKyc({ page: page + 1, limit, status, verificationStatus, search })
    );
  }, [dispatch, page, limit, status, verificationStatus, search]);

  return (
    <WhiteTitledCard>
      <div className="px-6">
        <div className="flex md:items-center justify-between flex-col lg:flex-row mb-4 gap-6">
          <div className="flex gap-4">
            <SearchInput
              placeholder="Search"
              onChange={(e: any) => setInputValue(e.target.value)}
              value={inputValue}
              onKeyDown={handleKeyDown}
            />
            <Button
              layout="toolbar" size="sm"
              label="filter"
              onClick={() => setSearch(inputValue)}
              classNames="hidden sm:flex"
            />
          </div>
          <div className="flex-col md:flex-row md:items-center gap-4 flex">
            <div className="flex gap-2">
              <FilterByButton
                options={kycStatusOption}
                onSelect={(value) => {
                  setStatus(value);
                  dispatch(clearAnalyticsData());
                }}
                buttonLabel={"KYC status"}
              />
              <FilterByButton
                options={verificationStatusOption}
                onSelect={(value) => {
                  setVerificationStatus(value);
                  dispatch(clearAnalyticsData());
                }}
                buttonLabel={"Verification status"}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between mb-4">
          {tabList?.map((tab) => (
            <div
              key={tab.id}
              className={`space-x-2 cursor-pointer text-sm font-semibold p-2 text-black border-b-2 border-primary`}
            >
              <span>{tab.name}</span>
              <span className="bg-lightPurple px-2 text-accent rounded-md">
                {tab?.count}
              </span>
            </div>
          ))}
        </div>

        <CustomTable
          data={kycData}
          noRecordText="No KYC Submissions"
          loading={loading}
          showFooter={true}
          totalPages={allKyc?.pagination?.totalPages}
          itemsPerPage={allKyc?.pagination?.limit}
          currentPage={page}
          onPageChange={(value: number) => setPage(value)}
          onLimitChange={(value: number) => {
            setLimit(value);
            setPage(0);
          }}
        />
      </div>

      <ViewKycModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        data={selectedUser}
      />
      <ApproveKycModal
        isOpen={approveModalOpen}
        user={selectedUser}
        onClose={() => setApproveModalOpen(false)}
        onSave={() => {
          setApproveModalOpen(false);
          dispatch(resetSingleKyc());
          dispatch(
            fetchAllKyc({
              page: page + 1,
              limit,
              status,
              verificationStatus,
              search,
            })
          );
        }}
      />
      <RejectKycModal
        isOpen={rejectModalOpen}
        user={selectedUser}
        onClose={() => setRejectModalOpen(false)}
        onSave={() => {
          setRejectModalOpen(false);
          dispatch(resetSingleKyc());
          dispatch(
            fetchAllKyc({
              page: page + 1,
              limit,
              status,
              verificationStatus,
              search,
            })
          );
        }}
      />
    </WhiteTitledCard>
  );
};

export default KYCTable;
